import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { formatZAR } from "@/lib/constants";
import { LogOut, Car, Star, TrendingUp, MapPin, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useGeolocation } from "@/hooks/useGeolocation";
import NotificationStatusCard from "@/components/NotificationStatusCard";

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", skills: "" });
  const [coords, setCoords] = useState(null);
  const [savingGPS, setSavingGPS] = useState(false);
  const geo = useGeolocation({ auto: false });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        skills: (user.skills || []).join(", "),
      });
      if (user.location) setCoords(user.location);
    }
  }, [user]);

  // When the hook resolves a fresh position, persist it and refresh user.
  useEffect(() => {
    if (geo.state === "granted" && geo.coords && !savingGPS) {
      const loc = { lat: geo.coords.lat, lng: geo.coords.lng };
      setCoords(loc);
      setSavingGPS(true);
      api.put("/users/me", { location: loc })
        .then(() => refreshUser())
        .then(() => toast.success("Location updated", {
          description: `Accuracy ±${Math.round(geo.coords.accuracy || 0)}m`,
        }))
        .catch(() => toast.error("Couldn't save location"))
        .finally(() => setSavingGPS(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.state, geo.coords?.lat, geo.coords?.lng]);

  if (!user) return null;

  const save = async (e) => {
    e.preventDefault();
    // Normalize SA phone if provided
    let phone = form.phone;
    if (phone) {
      const d = phone.replace(/[^\d+]/g, "");
      let rest = "";
      if (d.startsWith("+27")) rest = d.slice(3);
      else if (d.startsWith("27")) rest = d.slice(2);
      else if (d.startsWith("0")) rest = d.slice(1);
      if (rest.length !== 9 || !/^\d+$/.test(rest)) {
        toast.error("Invalid phone. Use +27 82 555 0100 or 082 555 0100.");
        return;
      }
      phone = `+27${rest}`;
    }
    try {
      await api.put("/users/me", {
        name: form.name,
        phone,
        skills: user.role === "mechanic" ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
      });
      await refreshUser();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const updateGPS = () => geo.request();

  const doLogout = () => {
    localStorage.clear();
    window.location.href = "/app";
  };

  return (
    <div>
      <div className="px-6 pt-8 pb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-1">
          {user.role.toUpperCase()}
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight">{user.name}</h1>
        <p className="text-sm text-[#9CA3AF]">{user.email}</p>
      </div>

      {user.role === "mechanic" && (
        <div className="px-6 grid grid-cols-3 gap-2 mb-4">
          <Stat icon={<TrendingUp size={16} />} label="EARNINGS" value={formatZAR(user.earnings)} yellow />
          <Stat icon={<Star size={16} />} label="RATING" value={user.rating ? `${user.rating}★` : "—"} />
          <Stat icon={<MapPin size={16} />} label="STATUS" value={user.availability ? "ONLINE" : "OFFLINE"} />
        </div>
      )}

      {user.role === "client" && (
        <div className="px-6 mb-4">
          <Link to="/app/vehicles" className="flex items-center justify-between border border-white/10 rounded-md p-4 hover:border-white" data-testid="profile-vehicles-link">
            <div className="flex items-center gap-3">
              <Car size={18} className="text-[#3CFF7E]" />
              <span className="font-display font-bold">My Vehicles</span>
            </div>
            <span className="text-[#9CA3AF]">→</span>
          </Link>
        </div>
      )}

      <form onSubmit={save} className="px-6 space-y-4">
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]" />
        </Field>
        <Field label="Phone">
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]" />
        </Field>
        {user.role === "mechanic" && (
          <Field label="Skills (comma separated)">
            <input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="profile-skills-input" className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]" />
          </Field>
        )}

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Location</label>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 h-12 border border-white/20 rounded-md px-4 flex items-center gap-2 text-sm">
              {(geo.state === "requesting" || savingGPS) ? (
                <Loader2 size={14} className="text-[#3CFF7E] animate-spin" data-testid="profile-gps-spinner" />
              ) : (geo.state === "denied" || geo.state === "error") ? (
                <AlertCircle size={14} className="text-[#E6FF3C]" />
              ) : (
                <MapPin size={14} className="text-[#3CFF7E]" />
              )}
              <span className="truncate">
                {geo.state === "requesting" || savingGPS
                  ? "Finding your location…"
                  : coords?.lat
                    ? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`
                    : "Not set"}
              </span>
            </div>
            <button
              type="button"
              onClick={updateGPS}
              disabled={geo.state === "requesting" || savingGPS}
              data-testid="update-gps-btn"
              className="h-12 px-4 border border-white rounded-md text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-60"
            >
              <RefreshCw size={12} /> Update GPS
            </button>
          </div>
          {geo.state === "denied" && (
            <div className="text-[11px] text-[#E6FF3C] mt-2">
              Please allow location access so nearby clients can find you. Tap the lock icon in the address bar → Permissions → Location → Allow.
            </div>
          )}
          {geo.state === "error" && (
            <div className="text-[11px] text-[#E6FF3C] mt-2">{geo.error || "Couldn't get your location."}</div>
          )}
        </div>

        <button type="submit" data-testid="profile-save-btn" className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md">
          Save Changes
        </button>
      </form>

      <div className="px-6 mt-6">
        <NotificationStatusCard />
      </div>

      <div className="px-6 mt-4 pb-6">
        <button onClick={doLogout} data-testid="logout-btn" className="w-full h-12 border border-white/20 text-white flex items-center justify-center gap-2 rounded-md font-display font-bold uppercase tracking-wider text-sm">
          <LogOut size={16} /> Sign Out
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">{label}</label>
      {children}
    </div>
  );
}

function Stat({ icon, label, value, yellow }) {
  if (yellow) {
    return (
      <div className="border border-[#E6FF3C]/40 bg-[#E6FF3C]/5 rounded-md p-3">
        <div className="text-[#E6FF3C] mb-1">{icon}</div>
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#E6FF3C] font-display">{label}</div>
        <div className="font-display font-black text-sm mt-0.5 text-[#E6FF3C]">{value}</div>
      </div>
    );
  }
  return (
    <div className="border border-white/10 bg-[#111827] rounded-md p-3">
      <div className="text-[#3CFF7E] mb-1">{icon}</div>
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">{label}</div>
      <div className="font-display font-black text-sm mt-0.5 text-white">{value}</div>
    </div>
  );
}
